/**
 * Audit log (added — see docs/BENCHMARKS.md).
 *
 * Shows field-level before/after rather than a bare "record updated", because the
 * question a reviewer or a disputed-commission enquiry actually asks is *what changed*.
 */

import * as React from 'react';
import type { AuditLogDto } from '@ias/shared';

import { Badge } from '@/components/ui/badge';
import { DataTable, type DataColumn } from '@/components/data-table/data-table';
import { EnumBadge } from '@/components/common/enum-badge';
import { PageHeader } from '@/components/common/page-header';
import { FilterBar, type FilterDescriptor } from '@/components/filters/filter-bar';
import { auditApi, useUsers } from '@/lib/api/domains';
import { useI18n } from '@/lib/i18n';
import { useListState } from '@/hooks/use-list-state';

const ENTITY_TYPES = [
  'client',
  'policy',
  'claim',
  'reminder',
  'user',
  'insurance_company',
  'document',
  'endorsement',
  'installment',
  'commission_settlement',
  'auth',
  // Agency-wide settings — currently the renewal-email template and its dispatch mode, which
  // is the one setting in the app that makes the system write to clients on its own.
  'settings',
];

export function AuditPage() {
  const { t, dateTime } = useI18n();
  const list = useListState({ defaultSortBy: 'createdAt', defaultSortOrder: 'desc' });
  const { data, isLoading, isFetching, error, refetch } = auditApi.useList(list.params);
  const { data: users } = useUsers();

  const columns = React.useMemo<DataColumn<AuditLogDto>[]>(
    () => [
      {
        id: 'createdAt',
        header: t('common.createdAt'),
        meta: { numeric: true, headerLabel: t('common.createdAt') },
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-xs">{dateTime(row.original.createdAt)}</span>
        ),
      },
      {
        id: 'userName',
        header: t('audit.user'),
        meta: { headerLabel: t('audit.user') },
        cell: ({ row }) => row.original.userName ?? <span className="text-muted-foreground">—</span>,
      },
      {
        id: 'action',
        header: t('audit.action'),
        meta: { headerLabel: t('audit.action') },
        cell: ({ row }) => <EnumBadge enum="auditAction" value={row.original.action} size="sm" />,
      },
      {
        id: 'entityType',
        header: t('audit.entity'),
        meta: { headerLabel: t('audit.entity') },
        cell: ({ row }) => (
          <Badge tone="outline" size="sm">
            {/* The log stores raw table names; `t` falls back to the key itself, so an
                entity we have not named yet still renders instead of disappearing. */}
            {t(`audit.entity.${row.original.entityType}`)}
          </Badge>
        ),
      },
      {
        id: 'changes',
        header: t('audit.changes'),
        meta: { headerLabel: t('audit.changes') },
        cell: ({ row }) => <ChangeSummary changes={row.original.changes} />,
      },
      {
        id: 'ipAddress',
        header: 'IP',
        meta: { numeric: true, hideBelow: 'xl', headerLabel: 'IP' },
        cell: ({ row }) => (
          <span className="text-muted-foreground text-xs">{row.original.ipAddress ?? '—'}</span>
        ),
      },
    ],
    [t, dateTime],
  );

  /*
   * One declaration, two homes: a filter naming a `column` is rendered in the table's
   * filter row under that column, the rest stay in the toolbar, and the narrow-screen
   * popover keeps all of them.
   */
  const filters: FilterDescriptor[] = [
    {
      key: 'entityType',
      column: 'entityType',
      type: 'options',
      label: t('audit.entity'),
      options: ENTITY_TYPES.map((entity) => ({ value: entity, label: entity })),
    },
    { key: 'action', type: 'enum', enum: 'auditAction', label: t('audit.action'), column: 'action' },
    {
      key: 'userId',
      column: 'userName',
      type: 'options',
      label: t('audit.user'),
      options: (users ?? []).map((user) => ({ value: user.id, label: user.fullName })),
    },
    { key: 'dateFrom', type: 'date', label: t('common.from'), column: 'createdAt' },
    { key: 'dateTo', type: 'date', label: t('common.to') },
  ];

  return (
    <>
      <PageHeader title={t('audit.title')} description={t('audit.subtitle')} />

      <FilterBar
        className="mb-5"
        listState={list}
        inline="unbound"
        searchPlaceholder={t('audit.user')}
        filters={filters}
      />

      <DataTable
        columns={columns}
        rows={data?.data}
        total={data?.meta.total}
        listState={list}
        filters={filters}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        onRetry={refetch}
        getRowId={(entry) => entry.id}
      />
    </>
  );
}

function ChangeSummary({ changes }: { changes: AuditLogDto['changes'] }) {
  const { t } = useI18n();
  const [expanded, setExpanded] = React.useState(false);

  if (!changes || Object.keys(changes).length === 0) {
    return <span className="text-muted-foreground text-xs">{t('audit.noChanges')}</span>;
  }

  const entries = Object.entries(changes);
  const visible = expanded ? entries : entries.slice(0, 2);

  return (
    <div className="space-y-0.5">
      {visible.map(([field, change]) => (
        <div key={field} className="flex flex-wrap items-baseline gap-1 text-xs">
          <span className="font-medium">{t(`audit.field.${field}`)}</span>
          <span className="text-muted-foreground line-through">{format(change.from)}</span>
          <span className="text-muted-foreground">→</span>
          <span className="font-medium">{format(change.to)}</span>
        </div>
      ))}
      {entries.length > 2 && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setExpanded((current) => !current);
          }}
          className="text-primary text-xs hover:underline"
        >
          {expanded ? '−' : `+${entries.length - 2}`}
        </button>
      )}
    </div>
  );
}

function format(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? '✓' : '✕';
  if (Array.isArray(value)) return value.join(', ') || '—';
  if (typeof value === 'object') return JSON.stringify(value);
  const text = String(value);
  return text.length > 28 ? `${text.slice(0, 28)}…` : text;
}
